import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Printer, Download, Loader2, AlertCircle } from 'lucide-react';
import comprobanteRetencionIVATemplateHtml from './ComprobanteRetencionIVATemplate';

const MAX_RETRIES = 50;

const AGENTE_GAMAN = {
  nombre: 'ELECTROSOLUCIONES GAMAN C.A',
  rif: 'J-411293229',
  direccion: 'CALLE ROSCIO EDIF RESIDENCIAS MARIANA PISO PB LOCAL 3 SECTOR CENTRO SAN JUAN DE LOS MORROS GUARICO ZONA POSTAL 2301'
};

// '01' = Registro de factura (tipos_transaccion_seniat) -- unica que hoy
// generan crearEmitidaIVA/ISLR, que solo operan contra una factura_compra
// directa, nunca contra una nota de debito/credito.
const TIPO_TRANSACCION_LABEL = { '01': '01-Reg', '02': '02-N/D', '03': '03-N/C', '04': '04-Sust', '05': '05-Anul' };

const fmtFecha = (f) => (f ? new Date(f).toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
// El comprobante de retencion es un documento fiscal venezolano: siempre
// en bolivares (Bs.S), sin importar en que moneda se facturo la compra.
const fmtBs = (montoOriginal, tasa) => `Bs.S${((Number(montoOriginal) || 0) * tasa).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${((Number(n) || 0) * 100).toFixed(2)}%`;

// Retencion IVA real (direccion='emitida') que Gaman emite a un proveedor:
// Gaman es el agente de retencion, el proveedor es el sujeto retenido. Un
// solo documento (retenciones_exige_documento) => una sola fila.
const transformData = (retencion) => {
  if (!retencion || !retencion.comprobante) return null;
  // Sin tasa no hay como convertir a bolivares sin inventar un numero
  // (migracion 018 la exige en toda factura de compra nueva; si esta
  // retencion es de antes de esa migracion, falla en vez de mentir).
  if (!retencion.tasa) return { error: 'sin_tasa' };

  const tasa = parseFloat(retencion.tasa);
  const periodoAnio = retencion.periodo ? retencion.periodo.slice(0, 4) : '';
  const periodoMes = retencion.periodo ? retencion.periodo.slice(4, 6) : '';

  const totalComprasIVA = parseFloat(retencion.fc_total || 0);
  const baseImponible = parseFloat(retencion.fc_base_imponible || 0);
  const montoExento = parseFloat(retencion.fc_base_exenta || 0);
  const impuestoIVA = parseFloat(retencion.fc_impuesto || 0);
  const ivaRetenido = parseFloat(retencion.monto || 0);
  const netoAPagarOriginal = totalComprasIVA - ivaRetenido;

  const operaciones = [{
    nro: 1,
    fecha: fmtFecha(retencion.fc_fecha_emision),
    numeroComprobante: retencion.comprobante,
    numeroFactura: retencion.fc_numero_proveedor || '',
    numeroControl: retencion.fc_numero_control_proveedor || '',
    tipoTransaccion: TIPO_TRANSACCION_LABEL[retencion.tipo_transaccion] || '',
    totalComprasIVA: fmtBs(totalComprasIVA, tasa),
    montoExento: montoExento ? fmtBs(montoExento, tasa) : '',
    baseImponible: fmtBs(baseImponible, tasa),
    porcentajeAlicuota: fmtPct(retencion.fc_iva_porcentaje_aplicado),
    impuestoIVA: fmtBs(impuestoIVA, tasa),
    porcentajeRetencion: fmtPct(retencion.porcentaje_aplicado),
    ivaRetenido: fmtBs(ivaRetenido, tasa)
  }];

  return {
    agente: AGENTE_GAMAN,
    sujetoRetenido: {
      nombre: retencion.proveedor_nombre || '',
      rif: retencion.proveedor_rif || ''
    },
    comprobante: {
      numero: retencion.comprobante,
      fechaEmision: fmtFecha(retencion.fecha),
      periodoAnio,
      periodoMes,
      nroComprobante: retencion.comprobante
    },
    operaciones,
    totales: {
      totalComprasIVA: fmtBs(totalComprasIVA, tasa),
      baseImponible: fmtBs(baseImponible, tasa),
      impuestoIVA: fmtBs(impuestoIVA, tasa),
      ivaRetenido: fmtBs(ivaRetenido, tasa)
    },
    footer: {
      netoAPagar: fmtBs(netoAPagarOriginal, tasa),
      fechaEntrega: fmtFecha(retencion.entregado_at) || fmtFecha(retencion.fecha)
    }
  };
};

const RetencionEmitidaIVAPrintModal = ({ onClose, retencion }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const datosInyectados = useRef(false);
  const retryCount = useRef(0);

  const datosCrudos = retencion ? transformData(retencion) : null;
  const datosComprobante = datosCrudos && !datosCrudos.error ? datosCrudos : null;

  const inyectarDatos = useCallback(() => {
    if (!iframeRef.current || datosInyectados.current || !datosComprobante) return;
    try {
      const win = iframeRef.current.contentWindow;
      if (win && typeof win.renderComprobanteRetencionIVA === 'function') {
        win.renderComprobanteRetencionIVA(datosComprobante);
        datosInyectados.current = true;
        setLoading(false);
      } else if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        setTimeout(inyectarDatos, 100);
      } else {
        setError('No se pudo cargar el comprobante (tiempo de espera agotado)');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error al inyectar datos:', err);
      setError('Error al renderizar el comprobante');
      setLoading(false);
    }
  }, [datosComprobante]);

  useEffect(() => {
    if (!datosComprobante) {
      setError(
        !retencion
          ? 'No hay retención para generar el comprobante'
          : datosCrudos?.error === 'sin_tasa'
          ? 'Esta factura de compra no tiene tasa de cambio registrada. Sin ella no se puede convertir el comprobante a bolívares.'
          : 'Esta retención todavía no fue emitida (sin número de comprobante). Emítala antes de generar el PDF.'
      );
      setLoading(false);
    }
  }, [datosComprobante, datosCrudos, retencion]);

  const handleIframeLoad = useCallback(() => {
    inyectarDatos();
  }, [inyectarDatos]);

  const handlePrint = () => {
    if (iframeRef.current) iframeRef.current.contentWindow.print();
  };

  const handleDownload = () => {
    if (iframeRef.current) {
      if (typeof iframeRef.current.contentWindow.descargarPDF === 'function') {
        iframeRef.current.contentWindow.descargarPDF();
      } else {
        iframeRef.current.contentWindow.print();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gray-100 border-b p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Printer className="text-gray-600" />
              Comprobante Retención IVA (proveedor)
            </h2>
            <p className="text-sm text-gray-500">Vista previa del comprobante</p>
          </div>
          <div className="flex gap-2">
            {!loading && !error && (
              <>
                <button onClick={handleDownload} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2">
                  <Download size={18} /> Descargar PDF
                </button>
                <button onClick={handlePrint} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2">
                  <Printer size={18} /> Imprimir
                </button>
              </>
            )}
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium flex items-center gap-2">
              <X size={18} /> Cerrar
            </button>
          </div>
        </div>

        <div className="flex-1 bg-gray-200 relative overflow-hidden flex justify-center items-center">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
              <Loader2 size={48} className="animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium text-gray-700">Generando documento...</p>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
              <AlertCircle size={64} className="text-red-500 mb-4" />
              <p className="text-xl font-bold text-gray-800 mb-2">No se puede mostrar el comprobante</p>
              <p className="text-gray-600 max-w-md text-center">{error}</p>
            </div>
          )}

          <iframe
            ref={iframeRef}
            srcDoc={comprobanteRetencionIVATemplateHtml}
            onLoad={handleIframeLoad}
            className={`w-full h-full bg-white transition-opacity duration-300 ${loading || error ? 'opacity-0' : 'opacity-100'}`}
            title="Comprobante Retención IVA (proveedor)"
          />
        </div>
      </div>
    </div>
  );
};

export default RetencionEmitidaIVAPrintModal;
